import React, { useState, useEffect } from 'react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import Sidebar from '../components/shared/Sidebar';
import { subscribeTasks, subscribeShipments } from '../firebase/firestore';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend, Filler);

const PERIODS = [
  { key:'today', label:'Today'      },
  { key:'week',  label:'This week'  },
  { key:'month', label:'This month' },
  { key:'year',  label:'This year'  },
  { key:'all',   label:'All time'   },
];

const TASK_COLORS = { urgent:'#EF4444','in-progress':'#185FA5',review:'#9333EA',pending:'#F59E0B',done:'#22C55E' };
const SHIP_COLORS = { Pending:'#6B7280','In Transit':'#185FA5',Arrived:'#639922','Customs Clearance':'#F59E0B',Cleared:'#14B8A6',Delivered:'#22C55E','On Hold':'#EF4444' };
const MEMBER_PAL  = ['#185FA5','#9333EA','#22C55E','#F59E0B','#EF4444'];

const BASE = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position:'bottom', labels:{ boxWidth:10, padding:14, font:{ size:11 } } },
    tooltip: { bodyFont:{ size:12 } },
  },
};
const SCALES = {
  y: { beginAtZero:true, grid:{ color:'rgba(0,0,0,.05)' }, ticks:{ precision:0, font:{ size:11 } } },
  x: { grid:{ display:false }, ticks:{ font:{ size:11 } } },
};

// ─── Helpers ──────────────────────────────────────────────────
function getPeriodStart(period) {
  const now = new Date();
  switch (period) {
    case 'today': return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    case 'week':  { const d = new Date(now); d.setDate(now.getDate() - now.getDay() + 1); d.setHours(0,0,0,0); return d; }
    case 'month': return new Date(now.getFullYear(), now.getMonth(), 1);
    case 'year':  return new Date(now.getFullYear(), 0, 1);
    default:      return new Date(0);
  }
}

function computeKpi(tasks, period) {
  const now   = new Date();
  const start = getPeriodStart(period);

  const inPeriod = t => {
    if (!t.createdAt?.toDate) return period === 'all';
    return t.createdAt.toDate() >= start;
  };

  const periodTasks = tasks.filter(inPeriod);
  const total  = periodTasks.length;
  const done   = periodTasks.filter(t => t.status === 'done').length;
  const overdue = tasks.filter(t => t.dueDate && new Date(t.dueDate) < now && t.status !== 'done').length;
  const rate   = total ? Math.round(done / total * 100) : 0;
  return { total, done, overdue, rate };
}

function computeTrend(tasks, period) {
  const now = new Date();

  const getCreated   = t => t.createdAt?.toDate?.() || null;
  const getCompleted = t => (t.status === 'done' && t.completedAt?.toDate?.()) ? t.completedAt.toDate() : null;
  const sameDay      = (a, b) => a && b && a.toDateString() === b.toDateString();

  if (period === 'today') {
    const hourStarts = [8,10,12,14,16,18];
    const labels = ['8am','10am','12pm','2pm','4pm','6pm'];
    const mkBucket = (getDate) => hourStarts.map((h, i) => {
      const lo = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h);
      const hi = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hourStarts[i+1] || 24);
      return tasks.filter(t => { const d = getDate(t); return d && d >= lo && d < hi; }).length;
    });
    return { labels, created: mkBucket(getCreated), done: mkBucket(getCompleted) };
  }

  if (period === 'week') {
    const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay() + 1); weekStart.setHours(0,0,0,0);
    const labels = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    const days   = labels.map((_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
    return {
      labels,
      created: days.map(day => tasks.filter(t => sameDay(getCreated(t), day)).length),
      done:    days.map(day => tasks.filter(t => sameDay(getCompleted(t), day)).length),
    };
  }

  if (period === 'month') {
    const labels     = ['Week 1','Week 2','Week 3','Week 4'];
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const weekOf     = d => Math.min(Math.floor((d.getDate() - 1) / 7), 3);
    const created = [0,0,0,0]; const done = [0,0,0,0];
    tasks.forEach(t => {
      const c = getCreated(t);
      if (c && c >= monthStart && c.getMonth() === now.getMonth()) created[weekOf(c)]++;
      const f = getCompleted(t);
      if (f && f >= monthStart && f.getMonth() === now.getMonth()) done[weekOf(f)]++;
    });
    return { labels, created, done };
  }

  if (period === 'year') {
    const labels    = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const created = new Array(12).fill(0); const done = new Array(12).fill(0);
    tasks.forEach(t => {
      const c = getCreated(t);
      if (c && c >= yearStart) created[c.getMonth()]++;
      const f = getCompleted(t);
      if (f && f >= yearStart) done[f.getMonth()]++;
    });
    return { labels, created, done };
  }

  // all time — group by year
  const years = [...new Set(tasks.filter(t => getCreated(t)).map(t => getCreated(t).getFullYear()))].sort();
  if (!years.length) return { labels:['2026'], created:[0], done:[0] };
  return {
    labels:  years.map(String),
    created: years.map(yr => tasks.filter(t => getCreated(t)?.getFullYear() === yr).length),
    done:    years.map(yr => tasks.filter(t => getCompleted(t)?.getFullYear() === yr).length),
  };
}

export default function AdminAnalytics() {
  const [period, setPeriod] = useState('month');
  const [tasks,  setTasks]  = useState([]);
  const [ships,  setShips]  = useState([]);

  useEffect(() => {
    const u1 = subscribeTasks(setTasks);
    const u2 = subscribeShipments(setShips);
    return () => { u1(); u2(); };
  }, []);

  const kpi   = computeKpi(tasks, period);
  const trend = computeTrend(tasks, period);

  // Task status doughnut
  const statusCounts = {};
  tasks.forEach(t => { statusCounts[t.status] = (statusCounts[t.status] || 0) + 1; });
  const doughnutData = {
    labels: Object.keys(statusCounts),
    datasets: [{ data:Object.values(statusCounts), backgroundColor:Object.keys(statusCounts).map(s => TASK_COLORS[s]||'#6B7280'), borderWidth:2, borderColor:'#fff' }],
  };

  // Task completion trend
  const lineData = {
    labels: trend.labels,
    datasets: [
      { label:'Completed', data:trend.done,    borderColor:'#22C55E', backgroundColor:'rgba(34,197,94,.1)',  fill:true, tension:0.4, pointRadius:4, pointBackgroundColor:'#22C55E' },
      { label:'Created',   data:trend.created, borderColor:'#185FA5', backgroundColor:'rgba(24,95,165,.06)', fill:true, tension:0.4, borderDash:[5,3], pointRadius:3, pointBackgroundColor:'#185FA5' },
    ],
  };

  // Tasks by team member
  const memberCounts = {};
  tasks.forEach(t => { if (t.assigneeName) memberCounts[t.assigneeName] = (memberCounts[t.assigneeName]||0)+1; });
  const memberData = {
    labels: Object.keys(memberCounts),
    datasets: [{ label:'Tasks', data:Object.values(memberCounts), backgroundColor:Object.keys(memberCounts).map((_,i)=>MEMBER_PAL[i%MEMBER_PAL.length]), borderRadius:4 }],
  };

  // Shipments by status
  const shipCounts = {};
  ships.forEach(s => { shipCounts[s.status] = (shipCounts[s.status]||0)+1; });
  const shipData = {
    labels: Object.keys(shipCounts),
    datasets: [{ label:'Shipments', data:Object.values(shipCounts), backgroundColor:Object.keys(shipCounts).map(s=>SHIP_COLORS[s]||'#6B7280'), borderRadius:4 }],
  };

  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Reports & analytics</div>
            <div className="page-sub">Performance overview across tasks and shipments</div>
          </div>
          <button className="btn" onClick={() => window.print()}><i className="ti ti-printer" /> Print</button>
        </div>
        <div className="scroll">

          {/* Period selector */}
          <div style={{ display:'flex', gap:4, marginBottom:20, background:'var(--gray-100)', borderRadius:10, padding:4, width:'fit-content' }}>
            {PERIODS.map(p => (
              <button key={p.key} onClick={() => setPeriod(p.key)} style={{
                padding:'6px 16px', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer', border:'none',
                background: period===p.key ? '#fff' : 'transparent',
                color:      period===p.key ? 'var(--blue)' : 'var(--gray-500)',
                boxShadow:  period===p.key ? '0 1px 4px rgba(0,0,0,.1)' : 'none',
                transition:'all .15s',
              }}>{p.label}</button>
            ))}
          </div>

          {/* KPI cards — computed from real Firestore data */}
          <div className="metrics-grid" style={{ marginBottom:20 }}>
            <div className="metric blue">  <div className="metric-num">{kpi.total}</div>   <div className="metric-label">Tasks created</div></div>
            <div className="metric green"> <div className="metric-num">{kpi.done}</div>    <div className="metric-label">Completed</div></div>
            <div className="metric red">   <div className="metric-num">{kpi.overdue}</div> <div className="metric-label">Overdue</div></div>
            <div className="metric purple"><div className="metric-num">{kpi.rate}%</div>   <div className="metric-label">Completion rate</div></div>
            <div className="metric blue">  <div className="metric-num">{ships.length}</div><div className="metric-label">Active shipments</div></div>
          </div>

          {/* Row 1: Trend + Doughnut */}
          <div className="grid-2" style={{ marginBottom:20 }}>
            <div className="card">
              <div className="card-header"><span className="card-title"><i className="ti ti-trending-up" style={{ color:'var(--green)' }} /> Task completion trend</span></div>
              <div style={{ height:220 }}><Line data={lineData} options={{ ...BASE, scales:SCALES }} /></div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title"><i className="ti ti-chart-donut" style={{ color:'var(--purple)' }} /> Tasks by status</span></div>
              <div style={{ height:220 }}><Doughnut data={doughnutData} options={{ ...BASE, cutout:'60%' }} /></div>
            </div>
          </div>

          {/* Row 2: Team member + Shipment */}
          <div className="grid-2">
            <div className="card">
              <div className="card-header"><span className="card-title"><i className="ti ti-users" style={{ color:'var(--blue)' }} /> Tasks by team member</span></div>
              <div style={{ height:220 }}>
                <Bar data={memberData} options={{ ...BASE, indexAxis:'y', scales:{ x:SCALES.y, y:{ ...SCALES.x, grid:{ display:false } } } }} />
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title"><i className="ti ti-ship" style={{ color:'var(--blue)' }} /> Shipments by status</span></div>
              <div style={{ height:220 }}><Bar data={shipData} options={{ ...BASE, scales:SCALES }} /></div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
